<template>
  <section class="cart">
    <modal
      v-if="loading"
      no-close
      center-content
    >
      <loader-spinner :size="1.5" />
      <template #footer>
        <span v-html="$t('loading')" />
      </template>
    </modal>

    <header class="cart__header">
      <headline level="2">
        {{ $t(orderData.giftcardorder ? 'giftcardTitle' : 'title') }}
      </headline>

      <action
        color="primary"
        class="cart__download-button"
        @click="downloadAllTickets()"
      >
        <span v-html="$t(orderData.giftcardorder ? 'downloadAllGiftcards' : 'downloadAll')" />
        <icon type="file-pdf" />
      </action>
    </header>

    <div class="cart__content">
      <div
        v-if="orderData.giftcardorder && orderData.giftcards.articles"
        class="cart__ticket"
      >
        <h3 class="cart__subtitle">
          {{ $t('giftcardAdmission') }}
        </h3>

        <section
          v-if="orderData.merge"
          class="cart-table"
        >
          <header class="cart-table__row cart-table__row--header cart-table__row--download">
            <span class="cart-table__quantity">
              <small>{{ $t('table.quantity') }}</small>
            </span>
            <span class="cart-table__type">
              <small>{{ $t('table.giftcardType') }}</small>
            </span>
            <span class="cart-table__price">
              <small>{{ $t('table.price') }}</small>
            </span>
          </header>

          <div
            v-for="(barcodes_for_type, typeId) in orderData.giftcards.articles"
            :key="typeId"
            class="cart-table__row cart-table__row--vouchers"
          >
            <span class="cart-table__quantity-type-row">
              <span class="cart-table__quantity cart-table__quantity--count">
                {{ barcodes_for_type.length }}
              </span>
              <span class="cart-table__type">
                {{ articleTypes[$i18n.locale][typeId] }}
              </span>
            </span>
            <span class="cart-table__price">
              {{ humanPrice(giftcardPrice(typeId) * barcodes_for_type.length) }}&nbsp;€
            </span>
          </div>

          <footer
            v-if="orderData.giftcards.articles"
            class="cart-table__row"
          >
            <span class="cart-table__download">
              <span class="cart-table__download-button">
                <action @click="downloadTicket(firstGiftcard.barcode, true)">
                  {{ $t('table.downloadPDF') }}
                </action>
              </span>
            </span>
          </footer>
        </section>

        <section
          v-if="!orderData.merge"
          class="cart-table"
        >
          <header class="cart-table__row cart-table__row--header cart-table__row--download">
            <span class="cart-table__type">
              <small>{{ $t('table.giftcardType') }}</small>
            </span>
            <span class="cart-table__price">
              <small>{{ $t('table.price') }}</small>
            </span>
            <span
              v-if="!orderData.merge"
              class="cart-table__download"
            >
              <small>{{ $t('table.downloadTicket') }}</small>
            </span>
          </header>

          <template v-for="(barcodes_for_type, typeId) in orderData.giftcards.articles">
            <div
              v-for="barcode in barcodes_for_type"
              :key="barcode.barcode"
              class="cart-table__row cart-table__row--vouchers"
            >
              <span class="cart-table__quantity-type-row">
                <span class="cart-table__type">
                  {{ articleTypes[$i18n.locale][typeId] }}
                </span>
              </span>
              <span class="cart-table__price">
                {{ humanPrice(giftcardPrice(typeId)) }}&nbsp;€
              </span>
              <span class="cart-table__download">
                <span class="cart-table__download-button">
                  <action @click="downloadTicket(barcode.barcode)">
                    {{ $t('table.downloadPDF') }}
                  </action>
                </span>
              </span>
            </div>
          </template>
        </section>
      </div>

      <div
        v-if="orderData.tickets.articles"
        class="cart__ticket"
      >
        <h3 class="cart__subtitle">
          {{ $t('admission') }}
        </h3>

        <div
          v-if="orderData.tickets.gap !== GAP_GIFT_CARD"
          class="cart__datetime"
        >
          <span class="cart__date">
            {{ $t('selectedDate') }}: <strong>{{ humanDate(orderData.tickets.date) }}</strong>
          </span>
          <span class="cart__time">
            {{ $t('arrivalSlot') }}: <strong>{{ gapTime(orderData.tickets.gap) }}</strong>
          </span>
        </div>

        <section class="cart-table">
          <header class="cart-table__row cart-table__row--header cart-table__row--download">
            <span class="cart-table__type">
              <small>{{ $t('table.type') }}</small>
            </span>
            <span class="cart-table__price">
              <small>{{ $t('table.price') }}</small>
            </span>
            <span class="cart-table__download">
              <small>{{ $t('table.downloadTicket') }}</small>
            </span>
          </header>

          <template v-for="(barcodes_for_type, typeId) in orderData.tickets.articles">
            <div
              v-for="barcode in barcodes_for_type"
              :key="barcode.barcode"
              class="cart-table__row"
            >
              <span class="cart-table__type-price-row">
                <span class="cart-table__type">
                  {{ articleTypes[$i18n.locale][typeId] }}
                </span>
                <span class="cart-table__price">
                  <span class="cart-table__price-number">
                    {{ humanPrice(barcode.price) }}&nbsp;€
                  </span>
                </span>
              </span>
              <span class="cart-table__download">
                <span class="cart-table__download-button">
                  <action @click="downloadTicket(barcode.barcode)">
                    {{ $t('table.downloadPDF') }}
                  </action>
                </span>
              </span>
            </div>
          </template>
        </section>
      </div>

      <div
        v-if="orderData.tours.articles"
        class="cart__ticket"
      >
        <h3 class="cart__subtitle">
          {{ $t('guidedTour') }}
        </h3>

        <div
          class="cart__datetime"
        >
          <span class="cart__date">
            {{ $t('selectedDate') }}: <strong>{{ humanDate(orderData.tours.date) }}</strong>
          </span>
        </div>

        <section class="cart-table">
          <header class="cart-table__row cart-table__row--header cart-table__row--download">
            <span class="cart-table__type">
              <small>{{ $t('table.type') }}</small>
            </span>
            <span class="cart-table__tour-slot">
              <small>{{ $t('table.tourSlot') }}</small>
            </span>
            <span class="cart-table__price">
              <small>{{ $t('table.price') }}</small>
            </span>
            <span class="cart-table__download">
              <small>{{ $t('table.downloadTicket') }}</small>
            </span>
          </header>

          <template v-for="(barcodes_for_type, typeId) in orderData.tours.articles">
            <div
              v-for="barcode in barcodes_for_type"
              :key="barcode.barcode"
              class="cart-table__row"
            >
              <span class="cart-table__type-price-row">
                <span class="cart-table__type">
                  {{ articleTypes[$i18n.locale][typeId] }}
                </span>
                <span class="cart-table__tour-slot">
                  {{ barcode.from }} - {{ barcode.until }}
                </span>
                <span class="cart-table__price">
                  <span class="cart-table__price-number">
                    {{ humanPrice(barcode.price) }}&nbsp;€
                  </span>
                </span>
              </span>
              <span class="cart-table__download">
                <span class="cart-table__download-button">
                  <action @click="downloadTicket(barcode.barcode)">
                    {{ $t('table.downloadPDF') }}
                  </action>
                </span>
              </span>
            </div>
          </template>
        </section>
      </div>

      <div
        v-if="orderData.events.articles"
        class="cart__ticket"
      >
        <h3 class="cart__subtitle">
          {{ $t('eventAdmission') }}
        </h3>

        <div class="cart__datetime">
          <span class="cart__date">
            {{ $t('selectedDate') }}: <strong>{{ humanDate(orderData.events.date) }}</strong>
          </span>
          <span class="cart__time">
            {{ $t('arrivalEvent') }}:
            <strong>{{ orderData.events.start_time }} - {{ orderData.events.end_time }}</strong>
          </span>
        </div>

        <section class="cart-table">
          <header class="cart-table__row cart-table__row--header cart-table__row--download">
            <span class="cart-table__type">
              <small>{{ $t('table.type') }}</small>
            </span>
            <span class="cart-table__price">
              <small>{{ $t('table.price') }}</small>
            </span>
            <span class="cart-table__download">
              <small>{{ $t('table.downloadTicket') }}</small>
            </span>
          </header>

          <template v-for="(barcodes_for_type, typeId) in orderData.events.articles">
            <div
              v-for="barcode in barcodes_for_type"
              :key="barcode.barcode"
              class="cart-table__row"
            >
              <span class="cart-table__type-price-row">
                <span class="cart-table__type">
                  {{ articleTypes[$i18n.locale][typeId] }}
                </span>
                <span class="cart-table__price">
                  <span class="cart-table__price-number">
                    {{ humanPrice(barcode.price) }}&nbsp;€
                  </span>
                </span>
              </span>
              <span class="cart-table__download">
                <span class="cart-table__download-button">
                  <action @click="downloadTicket(barcode.barcode)">
                    {{ $t('table.downloadPDF') }}
                  </action>
                </span>
              </span>
            </div>
          </template>
        </section>
      </div>

      <div
        v-if="hasAnyVouchers"
        class="cart__ticket"
      >
        <h3 class="cart__subtitle">
          {{ $t('vouchers.title') }}
        </h3>

        <div
          v-if="!orderData.giftcardorder"
          class="cart__datetime"
        >
          <p class="cart__warning">
            {{ $t('vouchers.hint') }}
          </p>
        </div>

        <section class="cart-table">
          <header class="cart-table__row cart-table__row--header cart-table__row--download">
            <span class="cart-table__quantity">
              <small>{{ $t('table.quantity') }}</small>
            </span>
            <span class="cart-table__type">
              <small>{{ $t('table.type') }}</small>
            </span>
            <span class="cart-table__price">
              <small>{{ $t('table.price') }}</small>
            </span>
          </header>

          <div
            v-for="(barcodes_for_type, typeId) in orderData.vouchers.articles"
            :key="typeId"
            class="cart-table__row cart-table__row--vouchers"
          >
            <span class="cart-table__quantity-type-row">
              <span class="cart-table__quantity cart-table__quantity--count">
                {{ barcodes_for_type.length }}
              </span>
              <span class="cart-table__type">
                {{ voucherTitle(typeId) }}
              </span>
            </span>
            <span class="cart-table__price">
              {{ humanPrice(voucherPrice(typeId) * barcodes_for_type.length) }}&nbsp;€
            </span>
          </div>

          <footer
            v-if="orderData.vouchers.articles"
            class="cart-table__row"
          >
            <span class="cart-table__download">
              <span class="cart-table__download-button">
                <action @click="downloadTicket(firstVoucher.barcode, true)">
                  {{ $t('table.downloadPDF') }}
                </action>
              </span>
            </span>
          </footer>
        </section>
      </div>

      <template v-if="!orderData.giftcardorder">
        <div class="cart__header">
          <router-link :to="walletLink">
            <img
              :src="walletImageSrc"
              alt="Apple Wallet"
              width="160"
            >
          </router-link>
        </div>
        <h2>{{ $t('wallet.title') }}</h2>
        <p>
          <span v-html="$t('wallet.information')" /> <router-link :to="walletLink">{{ $t('wallet.linkText') }}</router-link>.
        </p>
        <p><small>{{ $t('wallet.trademark') }}</small></p>
      </template>
    </div>

    <div class="cart__footer">
      <action-link
        :href="$t('homepage.link')"
        color="secondary"
        @click="setCookie"
      >
        {{ $t('homepage.text') }}
      </action-link>
    </div>
  </section>
</template>

<script>
import cartMixin from '../mixins/cart'

import cookie from '../functions/cookie'
import timeFromGap from '../functions/timeFromGap'

import action from './action.vue'
import actionLink from './actionLink.vue'
import headline from './headline.vue'
import icon from './icon.vue'
import loaderSpinner from '../components/loaderSpinner.vue'
import modal from '../components/modal.vue'

import { BASE_URL, API_URL, GAP_GIFT_CARD } from '../settings.js'

export default {
  name: 'CCartBookingDownload',

  components: {
    action,
    actionLink,
    headline,
    icon,
    modal,
    loaderSpinner
  },

  mixins: [cartMixin],

  props: {
    orderData: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      loading: false
    }
  },

  computed: {
    GAP_GIFT_CARD: () => GAP_GIFT_CARD,
    isGiftCardOrder () {
      return (this.orderData.tickets.gap === GAP_GIFT_CARD || this.orderData.events.gap === GAP_GIFT_CARD)
    },

    hasAnyVouchers () {
      for (const voucherType in this.orderData.vouchers.articles) {
        if (voucherType in this.orderData.vouchers.articles) {
          return true
        }
      }

      return false
    },

    firstVoucher () {
      return Object.values(this.orderData.vouchers.articles)[0][0]
    },

    firstGiftcard () {
      return Object.values(this.orderData.giftcards.articles)[0][0]
    },

    referenceId () {
      return this.$store.state.orderReference || this.$route.params.referenceid
    },

    walletImageSrc () {
      return `${BASE_URL}static/img/apple-wallet-${this.$i18n.locale}.svg`
    },

    walletLink () {
      return {
        name: `booking-confirmation-wallet-${this.$i18n.locale}`,
        params: {
          referenceid: this.referenceId
        }
      }
    }
  },

  methods: {
    setCookie () {
      cookie()
    },

    gapTime: function (gap) {
      return timeFromGap(gap)
    },

    downloadTicket (barcode, combine = false) {
      let downloadUrl = `${API_URL}download/${barcode}/pdf/?download=1&language=${this.$i18n.locale}`

      if (combine === true) {
        downloadUrl += '&combine=1'
      }

      window.open(downloadUrl)
    },

    downloadAllTickets () {
      const downloadUrl = `${API_URL}download/${this.referenceId}/combined/?download=1&order_type=all&language=${this.$i18n.locale}`
      window.open(downloadUrl)
    },

    voucherTitle: function (voucherId) {
      const vouchers = this.$store.state.vouchers
      for (const voucherGroup in vouchers) {
        for (const voucherIndex in vouchers[voucherGroup]) {
          if (vouchers[voucherGroup][voucherIndex].id === parseInt(voucherId)) {
            return (this.$i18n.locale === 'de') ? vouchers[voucherGroup][voucherIndex].text : vouchers[voucherGroup][voucherIndex].text_en
          }
        }
      }
      return ''
    },

    voucherPrice: function (voucherId) {
      const vouchers = this.$store.state.vouchers
      for (const voucherGroup in vouchers) {
        for (const voucherIndex in vouchers[voucherGroup]) {
          if (vouchers[voucherGroup][voucherIndex].id === parseInt(voucherId)) {
            return vouchers[voucherGroup][voucherIndex].price
          }
        }
      }
      return 0
    },

    giftcardPrice: function (giftcardId) {
      for (const articleId in this.orderData.giftcards.articles) {
        if (articleId === giftcardId) {
          return this.orderData.giftcards.articles[articleId][0].price
        }
      }

      return 0
    }
  }
}
</script>

<i18n locale="en">
{
  "loading": "Loading! Please wait",
  "title": "Download your Tickets",
  "giftcardTitle": "Download your Gift Cards",
  "downloadAll": "Download <strong>all tickets</strong>",
  "downloadAllGiftcards": "Download <strong>all gift cards</strong>",
  "admission": "Tickets Miniatur Wunderland Hamburg",
  "guidedTour": "Experiences at Miniatur Wunderland Hamburg",
  "giftcardAdmission": "Gift Cards Miniatur Wunderland Hamburg",
  "eventAdmission": "Special events",
  "selectedDate": "Chosen Date",
  "arrivalSlot": "Time Slot of Arrival",
  "arrivalEvent": "Event time",
  "table": {
    "quantity": "Qty",
    "type": "Ticket Type",
    "giftcardType": "Gitftcard Type",
    "tourSlot": "Time",
    "price": "Price",
    "downloadTicket": "Download Ticket",
    "downloadGiftcard": "Download Giftcard",
    "downloadPDF": "Download"
  },
  "vouchers": {
    "title": "Vouchers",
    "type": "Type of voucher",
    "hint": "Attention: Please pick up your vouchers at the Miniatur Wunderland."
  },
  "homepage": {
    "text": "Go to Website",
    "link": "http://www.miniatur-wunderland.com/"
  },
  "wallet": {
    "title": "Save tickets on your smartphone",
    "information": "You can save your tickets on your iPhone or Android smartphone with the Apple Wallet App or <a href=\"https://play.google.com/store/search?q=wallet\" target=\"_blank\" rel=\"noopener\">any Android compatible app</a>. Click here to open the",
    "linkText": "download page for the Apple Wallet tickets",
    "trademark": "Apple and iPhone are trademarks of Apple Inc., registered in the U.S. and other countries."
  }
}
</i18n>

<i18n locale="de">
{
  "loading": "Laden! Bitte warten",
  "title": "Ihre Tickets zum Download",
  "giftcardTitle": "Ihre Geschenk Gutscheine zum Download",
  "downloadAll": "<strong>Alle Tickets</strong> herunterladen",
  "downloadAllGiftcards": "<strong>Alle Geschenkgutscheine</strong> herunterladen",
  "admission": "Eintrittskarten Miniatur Wunderland Hamburg",
  "guidedTour": "Erlebnisse im Miniatur Wunderland Hamburg",
  "giftcardAdmission": "Geschenkgutscheine Miniatur Wunderland Hamburg",
  "eventAdmission": "Sonderveranstaltungen",
  "selectedDate": "Gewähltes Datum",
  "arrivalSlot": "Einlasszeit",
  "arrivalEvent": "Veranstaltungszeit",
  "table": {
    "quantity": "Anzahl",
    "type": "Ticketart",
    "giftcardType": "Geschenkgutscheinart",
    "tourSlot": "Zeitraum",
    "price": "Preis",
    "downloadTicket": "Ticket-Download",
    "downloadGiftcard": "Geschenkgutschein-Download",
    "downloadPDF": "Herunterladen"
  },
  "vouchers": {
    "title": "Gutscheine",
    "type": "Gutscheinart",
    "hint": "Achtung: Bitte holen Sie die Gutscheine vorher im Miniatur Wunderland ab."
  },
  "homepage": {
    "text": "Zur Webseite",
    "link": "http://www.miniatur-wunderland.de/"
  },
  "wallet": {
    "title": "Tickets auf dem Smartphone speichern",
    "information": "Sie können sich Ihre Tickets auch bequem auf Ihrem iPhone oder Android Smartphone in der Apple Wallet App oder einer <a href=\"https://play.google.com/store/search?q=wallet\" target=\"_blank\" rel=\"noopener\">mit Android kompatiblen App</a> speichern. Hier gelangen Sie zur",
    "linkText": "Download-Seite für die Apple Wallet Tickets",
    "trademark": "Apple und iPhone sind Marken der Apple Inc., die in den USA und weiteren Ländern eingetragen sind."
  }
}
</i18n>
